import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Scale } from 'lucide-react';
import { useReconciliationFindings } from './hooks/useReconciliationFindings';
import { SideBySideDiffViewer } from './components/SideBySideDiffViewer';
import { ResolveFindingModal } from './components/ResolveFindingModal';
import { FindingKindBadge } from './components/FindingKindBadge';
import { FindingStatusBadge } from './components/FindingStatusBadge';
import type { ReconciliationFindingRow } from './types';

export default function ReconciliationFindingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useReconciliationFindings();

  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);

  const finding: ReconciliationFindingRow | undefined = data?.items.find((f) => f.id === id);

  if (isLoading) {
    return (
      <div className="p-12 text-center text-xs text-on-surface-variant">
        Loading finding details...
      </div>
    );
  }

  if (!finding) {
    return (
      <div className="p-12 text-center border border-border-subtle rounded-2xl glass-panel space-y-3">
        <Scale className="w-10 h-10 text-on-surface-variant mx-auto opacity-50" />
        <h3 className="text-sm font-bold text-on-surface">Finding Not Found</h3>
        <p className="text-xs text-on-surface-variant">
          Finding <code className="font-mono text-primary">{id}</code> could not be located or has been resolved and purged.
        </p>
        <Link
          to="/reconciliation"
          className="inline-flex items-center gap-1.5 text-xs text-primary font-bold hover:underline pt-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Reconciliation Dashboard</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px] mx-auto pb-12">
      {/* Top Nav */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/reconciliation')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-on-surface-variant hover:text-on-surface transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Reconciliation</span>
        </button>

        {!finding.resolvedAt && (
          <button
            type="button"
            onClick={() => setIsResolveModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs shadow-md transition-all"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Resolve Finding</span>
          </button>
        )}
      </div>

      {/* Main Container */}
      <div className="p-6 border border-border-subtle rounded-3xl glass-panel space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border-subtle">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <FindingKindBadge kind={finding.kind} />
              <FindingStatusBadge row={finding} />
            </div>
            <h1 className="text-xl font-bold font-display text-on-surface">
              {finding.entityType} Discrepancy
            </h1>
            <p className="text-xs font-mono text-on-surface-variant mt-0.5">
              ID: {finding.id} • Entity ID: {finding.entityId}
            </p>
          </div>

          <div className="text-right text-xs">
            <span className="text-on-surface-variant block">Detected At</span>
            <span className="font-mono font-semibold text-on-surface">
              {new Date(finding.detectedAt).toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        {/* Diff view inline */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
            Ledger & Gateway State Comparison
          </h3>
          <SideBySideDiffViewer
            finding={finding}
            onClose={() => navigate('/reconciliation')}
            onResolve={() => setIsResolveModalOpen(true)}
          />
        </div>
      </div>

      {/* Resolve Modal */}
      <ResolveFindingModal
        finding={finding}
        isOpen={isResolveModalOpen}
        onClose={() => setIsResolveModalOpen(false)}
        onSuccess={() => navigate('/reconciliation')}
      />
    </div>
  );
}
